import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { SERVICES, BOOKING_SOURCES } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { ArrowLeft, ChevronLeft, ChevronRight, Clock, Plus, Check } from "lucide-react";
import { unavailableRanges } from "@/components/crm/Availability";
import {
  SLOT_MINUTES, ANY_DOCTOR, BOOK_BRANCH_KEY, todayISO, computeSlots, bookableBranches, defaultBranch,
} from "@/lib/bookingSlots";
import { LeadSearchInput } from "@/components/crm/LeadSearchInput";
import MaskedPhoneInput from "@/components/crm/MaskedPhoneInput";
import { Field } from "@/pages/Leads";

export default function BookAppointment() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isDoctor = user?.role === "doctor";
  const [doctors, setDoctors] = useState([]);
  const [branches, setBranches] = useState([]);
  const [branchesReady, setBranchesReady] = useState(false);
  const [branchId, setBranchId] = useState(() => localStorage.getItem(BOOK_BRANCH_KEY) || "");
  const [selectedDoctor, setSelectedDoctor] = useState("");
  const [availability, setAvailability] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [date, setDate] = useState(todayISO());
  const [selectedSlot, setSelectedSlot] = useState(null); // {time: "HH:MM", iso: full ISO}
  const [bookOpen, setBookOpen] = useState(false);

  // Walk-in: someone is standing at the desk NOW. Skip the slot grid entirely
  // and open the booking form on the current time, rounded down to the slot
  // boundary — asking reception to hunt for "10:45" while the patient waits is
  // the whole reason they were still writing walk-ins in a paper book.
  const isWalkIn = new URLSearchParams(window.location.search).get("walkin") === "1";
  useEffect(() => {
    // Wait for branches, so the form opens already on the right branch.
    if (!isWalkIn || !branchesReady || bookOpen || selectedSlot) return;
    const now = new Date();
    const mins = Math.floor((now.getHours() * 60 + now.getMinutes()) / SLOT_MINUTES) * SLOT_MINUTES;
    const d = new Date();
    d.setHours(Math.floor(mins / 60), mins % 60, 0, 0);
    const h = d.getHours();
    setSelectedSlot({
      time: `${h % 12 === 0 ? 12 : h % 12}:${String(d.getMinutes()).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`,
      iso: d.toISOString(),
      timeStr: `${String(h).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`,
    });
    setBookOpen(true);
  }, [isWalkIn, branchesReady, bookOpen, selectedSlot]);

  useEffect(() => {
    if (isDoctor) {
      setDoctors(user ? [user] : []);
      setSelectedDoctor(user?.id || "");
    } else {
      const p = { role: "doctor" };
      api.get("/users", { params: p }).then((r) => {
        setDoctors(r.data);
        if (r.data.length === 1) setSelectedDoctor(r.data[0].id);
      }).catch(() => {});
    }
    api.get("/branches").then((r) => setBranches(r.data)).catch(() => {})
      .finally(() => setBranchesReady(true));
  }, [isDoctor, user]);

  // Branch-scoped users book into their own branches (BookingDialog's rule);
  // HQ into any. Remembered choice, else their only branch, else the first.
  const branchOptions = useMemo(() => bookableBranches(user, branches), [branches, user]);
  const branchObj = defaultBranch(branchOptions, user, branchId);
  const pickBranch = (v) => { setBranchId(v); localStorage.setItem(BOOK_BRANCH_KEY, v); };

  useEffect(() => {
    if (!selectedDoctor) { setAvailability(null); return; }
    api.get(`/users/${selectedDoctor}/availability`).then((r) => setAvailability(r.data)).catch(() => {});
  }, [selectedDoctor]);

  useEffect(() => {
    api.get("/appointments").then((r) => setAppointments(r.data)).catch(() => {});
  }, []);

  const shiftDay = (delta) => {
    const d = new Date(date + "T00:00:00");
    d.setDate(d.getDate() + delta);
    setDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`);
  };

  const docObj = doctors.find((d) => d.id === selectedDoctor);
  const niceDate = new Date(date + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  // Any doctor → the selected branch's hours/closed days; a doctor → their own
  // availability minus their bookings (lib/bookingSlots.computeSlots).
  const { slots, dayInfo, closedNote } = useMemo(
    () => computeSlots({ doctorId: selectedDoctor, availability, branch: branchObj, date, appointments }),
    [selectedDoctor, availability, date, appointments, branchObj]);

  const openBooking = (slot) => {
    if (!slot.available) return;
    const d = new Date(date + "T00:00:00");
    d.setHours(Math.floor(slot.minutes / 60), slot.minutes % 60, 0, 0);
    setSelectedSlot({ time: slot.label, iso: d.toISOString(), timeStr: slot.timeStr });
    setBookOpen(true);
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate("/appointments")} className="h-8 w-8">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h2 className="font-display text-2xl font-semibold text-navy">Book Appointment</h2>
          <p className="text-sm text-muted-foreground">Pick a date and time. Choosing a doctor is optional.</p>
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-end gap-4">
        {!isDoctor && (
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Doctor</label>
            <Select value={selectedDoctor || ANY_DOCTOR} onValueChange={(v) => setSelectedDoctor(v === ANY_DOCTOR ? "" : v)}>
              <SelectTrigger className="h-9 w-56 bg-white" data-testid="book-doctor"><SelectValue /></SelectTrigger>
              <SelectContent>
                {/* Doctor is optional — reception often books before knowing who
                    will see the patient, and check-in is where it gets assigned. */}
                <SelectItem value={ANY_DOCTOR}>Any doctor — assign later</SelectItem>
                {doctors.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        {branchOptions.length > 0 && (
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Branch</label>
            <Select value={branchObj?.id || ""} onValueChange={pickBranch}>
              <SelectTrigger className="h-9 w-48 bg-white" data-testid="book-branch"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                {branchOptions.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => shiftDay(-1)}><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" className="h-9" onClick={() => setDate(todayISO())}>Today</Button>
          <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => shiftDay(1)}><ChevronRight className="h-4 w-4" /></Button>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value || todayISO())} className="h-9 w-40 bg-white" />
        </div>
      </div>

      {/* Date header */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold">
          {docObj ? docObj.name : "Any doctor"}{branchObj ? ` · ${branchObj.name}` : ""}
        </p>
        <h3 className="font-display text-xl font-semibold text-navy">{niceDate}</h3>
      </div>

      {/* Slot grid */}
      {!dayInfo ? (
        <div className="rounded-xl border border-navy/10 bg-white p-12 text-center text-muted-foreground">
          {docObj ? `${docObj.name} is not available on this day.` : (closedNote || "The clinic is closed on this day.")}
        </div>
      ) : (
        <div className="rounded-xl border border-navy/10 bg-white p-5 shadow-sm">
          {!selectedDoctor && dayInfo.fallback && (
            <p className="mb-3 text-xs text-amber-700" data-testid="book-branch-hours-fallback">Branch hours not set — using 9 AM–6 PM</p>
          )}
          <div className="mb-3 flex items-center gap-4 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5"><span className="inline-block h-3 w-3 rounded bg-green-100 border border-green-300" /> Available</span>
            <span className="flex items-center gap-1.5"><span className="inline-block h-3 w-3 rounded bg-red-50 border border-red-200" /> Booked</span>
            <span className="flex items-center gap-1.5"><span className="inline-block h-3 w-3 rounded bg-gray-100 border border-gray-200" /> Break / Past</span>
          </div>
          <div className="grid grid-cols-4 gap-2 sm:grid-cols-6 lg:grid-cols-8">
            {slots.map((slot) => (
              <button
                key={slot.minutes}
                onClick={() => openBooking(slot)}
                disabled={!slot.available}
                className={`rounded-lg border px-2 py-2.5 text-center text-sm font-medium transition-all
                  ${slot.available
                    ? "border-green-200 bg-green-50 text-green-800 hover:bg-green-100 hover:border-green-400 hover:shadow-sm cursor-pointer"
                    : slot.isBooked
                      ? "border-red-200 bg-red-50 text-red-400 cursor-not-allowed"
                      : "border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed"
                  }`}
              >
                {slot.label}
                {slot.isBooked && <p className="text-[9px] mt-0.5">Booked</p>}
                {slot.isBreak && <p className="text-[9px] mt-0.5">Break</p>}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Booking dialog */}
      {selectedSlot && (
        <BookingDialog
          open={bookOpen}
          onOpenChange={setBookOpen}
          doctorId={selectedDoctor}
          doctorName={docObj?.name || ""}
          date={date}
          slot={selectedSlot}
          doctors={doctors}
          branches={branches}
          userBranchIds={user?.branch_ids || []}
          initialBranchId={branchObj?.id || ""}
          onDone={() => {
            setBookOpen(false);
            api.get("/appointments").then((r) => setAppointments(r.data)).catch(() => {});
          }}
        />
      )}
    </div>
  );
}

function BookingDialog({ open, onOpenChange, doctorId, doctorName, date, slot, doctors, branches, userBranchIds = [], initialBranchId = "", canViewRevenue = true, onDone }) {
  // The page's branch (whose hours made the slot grid) wins.
  const prefillBranch = initialBranchId || (userBranchIds.length === 1 ? userBranchIds[0] : "");
  const [form, setForm] = useState({ patient_name: "", mobile: "", service: "", category: "", appointment_type: "in_clinic", branch_id: prefillBranch, duration_minutes: "15", lead_id: "", source: "", revenue: "", notes: "" });
  const [categories, setCategories] = useState([]);
  const [linkedLead, setLinkedLead] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Filter branches for branch-scoped roles
  // (plus whatever is selected, e.g. an HQ user's pick from the page, so the
  // Select never shows a value it has no option for)
  const filteredBranches = userBranchIds.length > 0
    ? branches.filter((b) => userBranchIds.includes(b.id) || b.id === form.branch_id) : branches;

  // Nobody was picked from the search, so as far as the form knows this is a new
  // person. (The backend still tries a last-10-digit mobile match before it
  // creates anything, so a source given here is simply ignored if they turn out
  // to be known.)
  const isNewPerson = !form.lead_id && !form.patient_id;

  useEffect(() => {
    if (open) {
      setForm({ patient_name: "", mobile: "", service: "", category: "", appointment_type: "in_clinic", branch_id: prefillBranch, duration_minutes: "15", lead_id: "", source: "", revenue: "", notes: "" });
      setLinkedLead(null);
      api.get("/master-data", { params: { field: "appointment_category" } })
        .then((r) => setCategories((r.data.appointment_category || []).map((c) => c.value)))
        .catch(() => {});
      // Default slot length is a clinic setting — this practice runs 10-minute
      // consults, and defaulting everyone to 15 made reception re-pick it every
      // single time.
      if (prefillBranch) {
        api.get(`/clinic-settings/${prefillBranch}`)
          .then((r) => {
            const d = r.data?.default_appointment_minutes;
            if (d) setForm((f) => ({ ...f, duration_minutes: String(d) }));
          })
          .catch(() => {});
      }
    }
  }, [open, prefillBranch]);

  // A result is either an existing PATIENT or a lead. Attaching patient_id when
  // it's a patient is what stops check-in minting a second record for someone
  // who is already mid-way through a package.
  const handleLeadSelect = (hit) => {
    setForm((f) => ({
      ...f,
      lead_id: hit.lead_id ?? (hit.kind === "lead" ? hit.id : null),
      patient_id: hit.patient_id || null,
      // They're known — their lead already has a source. Drop anything typed
      // before the match so the form state doesn't lie.
      source: "",
      patient_name: hit.name || f.patient_name,
      mobile: hit.mobile || f.mobile,
      service: hit.interested_service || f.service,
      branch_id: hit.branch_id || f.branch_id,
    }));
    setLinkedLead(hit);
  };

  const submit = async () => {
    if (!form.patient_name) { toast.error("Patient name is required"); return; }
    // Only blocks when a lead will actually be created — no mobile means the
    // backend can't dedupe, so it books an orphan and never asks for a source.
    if (isNewPerson && form.mobile && !form.source) {
      toast.error("Pick a source — this booking creates a new lead");
      return;
    }
    setSaving(true);
    try {
      await api.post("/appointments", {
        ...form,
        // Empty string would be stored verbatim and then never match a doctor
        // lookup; unassigned has to be a real null.
        doctor_id: doctorId || null,
        lead_id: form.lead_id || null,
        patient_id: form.patient_id || null,
        branch_id: form.branch_id || null,
        scheduled_at: slot.iso,
        duration_minutes: parseInt(form.duration_minutes, 10) || 15,
        revenue: form.revenue !== "" ? parseFloat(form.revenue) : null,
      });
      toast.success(doctorName
        ? `Booked ${form.patient_name} with ${doctorName} at ${slot.time}`
        : `Booked ${form.patient_name} at ${slot.time} — doctor assigned at check-in`);
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const niceDate = new Date(date + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Taller than a laptop screen: the body scrolls, header + buttons stay. */}
      <DialogContent className="sm:max-w-md flex flex-col max-h-[90vh]">
        <DialogHeader><DialogTitle className="font-display text-navy">Book Appointment</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2 overflow-y-auto flex-1 -mx-6 px-6">
          {/* Slot summary */}
          <div className="flex items-center gap-3 rounded-lg bg-green-50 border border-green-200 px-4 py-3">
            <Clock className="h-5 w-5 text-green-700" />
            <div>
              <p className="text-sm font-semibold text-green-800">{slot.time} · {niceDate}</p>
              <p className="text-xs text-green-600">{doctorName || "Doctor assigned at check-in"}</p>
            </div>
            <Check className="ml-auto h-5 w-5 text-green-600" />
          </div>

          <Field label="Patient Name *">
            <LeadSearchInput value={form.patient_name} onChange={(v) => set("patient_name", v)} onLeadSelect={handleLeadSelect} searchEndpoint="/booking-search" searchParam="q" placeholder="Search name or mobile, or type a new name..." />
          </Field>
          <Field label="Mobile">
            <MaskedPhoneInput value={form.mobile} onChange={(v) => set("mobile", v)} lockedValue={linkedLead?.mobile} placeholder="Phone number" />
          </Field>
          {/* Nobody matched, so this booking will CREATE the lead — ask where
              they came from. Hidden the moment an existing lead/patient is
              picked, since that lead already has a source. */}
          {isNewPerson && (
            <Field label="Source *">
              <Select value={form.source} onValueChange={(v) => set("source", v)}>
                <SelectTrigger data-testid="book-appt-source"><SelectValue placeholder="Where did they come from?" /></SelectTrigger>
                <SelectContent>{BOOKING_SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
              <p className="mt-1 text-xs text-muted-foreground">
                New enquiry — a lead will be created and assigned to you.
              </p>
            </Field>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Service">
              <Select value={form.service} onValueChange={(v) => set("service", v)}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{SERVICES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Duration">
              <Select value={form.duration_minutes} onValueChange={(v) => set("duration_minutes", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["10", "15", "30", "45", "60", "90"].map((m) => <SelectItem key={m} value={m}>{m} min</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {/* Category colour-codes the calendar and groups the appointments
                report; type separates in-clinic from video consults. */}
            <Field label="Category">
              <Select value={form.category || "__none__"}
                      onValueChange={(v) => set("category", v === "__none__" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Type">
              <Select value={form.appointment_type} onValueChange={(v) => set("appointment_type", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="in_clinic">In-clinic</SelectItem>
                  <SelectItem value="video">Video consultation</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Branch">
              <Select value={form.branch_id} onValueChange={(v) => set("branch_id", v)}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{filteredBranches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            {canViewRevenue && (
              <Field label="Revenue">
                <Input type="number" inputMode="decimal" value={form.revenue} onChange={(e) => set("revenue", e.target.value)} placeholder="₹ 0" />
              </Field>
            )}
          </div>
          <Field label="Remark">
            <Input value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Note / remark" />
          </Field>
        </div>
        <DialogFooter className="shrink-0 border-t border-navy/10 pt-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-navy text-white hover:bg-navy-hover">
            {saving ? "Booking..." : "Confirm Booking"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
